<template>
  <div class="financial">
    <!-- Hero Section -->
    <PageHero 
      :title="$t('financial.hero.title')"
      :subtitle="$t('financial.hero.subtitle')"
      :description="$t('financial.hero.description')"
      :showCta="true"
      :ctaText="$t('financial.dashboard.tryNow')"
    />

    <!-- Real-time Dashboard Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="flex flex-wrap items-center">
          <div class="w-full lg:w-1/2 mb-8 lg:mb-0" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <h2 class="text-3xl font-bold text-[#0f4640] mb-6">{{ $t('financial.dashboard.title') }}</h2>
            <p class="text-xl text-gray-600 mb-8">
              {{ $t('financial.dashboard.description') }}
            </p>
            <a href="https://admin.salasah.sa/#/login" class="px-8 py-4 bg-[#0f4640] text-[#faf6df] rounded-xl font-bold hover:bg-opacity-90 transition-all no-underline inline-flex items-center">
              {{ $t('financial.dashboard.tryNow') }} 
              <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
            </a>
          </div>
          <div class="w-full lg:w-1/2 lg:pl-12">
            <img src="https://erpnext.com/files/accounts-dashboard15be3f.png" :alt="$t('financial.dashboard.title')" 
              class="rounded-2xl shadow-lg w-full hover:scale-105 transition-transform cursor-pointer object-contain bg-white p-4">
          </div>
        </div>
      </div>
    </section>

    <!-- Main Features Grid -->
    <section class="py-20 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-[#0f4640] mb-6">{{ $t('financial.features.title') }}</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ $t('financial.features.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Journal Entries -->
          <div class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all overflow-hidden p-8">
            <div class="flex items-start justify-between mb-6">
              <div class="flex-1" :class="{ 'text-right': $i18n.locale === 'ar' }">
                <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center mb-4" :class="{ 'float-left ml-4': $i18n.locale === 'ar', 'float-right mr-4': $i18n.locale !== 'ar' }">
                  <i class="fas fa-book text-[#faf6df] text-xl"></i>
                </div>
                <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('financial.features.cards.journalEntries.title') }}</h3>
                <p class="text-gray-600 mb-4">
                  {{ $t('financial.features.cards.journalEntries.description') }}
                </p>
                <a href="https://admin.salasah.sa/#/login" class="text-[#0f4640] font-semibold hover:text-[#0f4640]/80 no-underline">
                  {{ $t('financial.features.cards.journalEntries.readMore') }}
                  <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
                </a>
              </div>
            </div>
            <div class="relative h-[300px] overflow-hidden rounded-xl">
              <img 
                src="https://erpnext.com/files/general-ledger.png" 
                :alt="$t('financial.features.cards.journalEntries.title')" 
                class="w-full h-full object-contain bg-gray-50 p-4"
              >
            </div>
          </div>

          <!-- Account Tree -->
          <div class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all overflow-hidden p-8">
            <div class="flex items-start justify-between mb-6">
              <div class="flex-1" :class="{ 'text-right': $i18n.locale === 'ar' }">
                <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center mb-4" :class="{ 'float-left ml-4': $i18n.locale === 'ar', 'float-right mr-4': $i18n.locale !== 'ar' }">
                  <i class="fas fa-sitemap text-[#faf6df] text-xl"></i>
                </div>
                <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('financial.features.cards.accountTree.title') }}</h3>
                <p class="text-gray-600 mb-4">
                  {{ $t('financial.features.cards.accountTree.description') }}
                </p>
                <a href="https://admin.salasah.sa/#/login" class="text-[#0f4640] font-semibold hover:text-[#0f4640]/80 no-underline">
                  {{ $t('financial.features.cards.accountTree.readMore') }}
                  <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
                </a>
              </div>
            </div>
            <div class="relative h-[300px] overflow-hidden rounded-xl">
              <img 
                src="https://erpnext.com/files/erpnext-chart-of-accounts.png" 
                :alt="$t('financial.features.cards.accountTree.title')" 
                class="w-full h-full object-contain bg-gray-50 p-4"
              >
            </div>
          </div>

          <!-- Billing -->
          <div class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all overflow-hidden p-8">
            <div class="flex items-start justify-between mb-6">
              <div class="flex-1" :class="{ 'text-right': $i18n.locale === 'ar' }">
                <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center mb-4" :class="{ 'float-left ml-4': $i18n.locale === 'ar', 'float-right mr-4': $i18n.locale !== 'ar' }">
                  <i class="fas fa-receipt text-[#faf6df] text-xl"></i>
                </div>
                <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('financial.features.cards.billing.title') }}</h3>
                <p class="text-gray-600 mb-4">
                  {{ $t('financial.features.cards.billing.description') }}
                </p>
                <a href="https://admin.salasah.sa/#/login" class="text-[#0f4640] font-semibold hover:text-[#0f4640]/80 no-underline">
                  {{ $t('financial.features.cards.billing.readMore') }}
                  <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
                </a>
              </div>
            </div>
            <div class="relative h-[300px] overflow-hidden rounded-xl">
              <img 
                src="https://erpnext.com/files/sales-invoice.png" 
                :alt="$t('financial.features.cards.billing.title')" 
                class="w-full h-full object-contain bg-gray-50 p-4"
                loading="lazy"
              >
            </div>
          </div>

          <!-- Multi-Currency -->
          <div class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all overflow-hidden p-8">
            <div class="flex items-start justify-between mb-6">
              <div class="flex-1" :class="{ 'text-right': $i18n.locale === 'ar' }">
                <div class="w-12 h-12 bg-[#0f4640] rounded-lg flex items-center justify-center mb-4" :class="{ 'float-left ml-4': $i18n.locale === 'ar', 'float-right mr-4': $i18n.locale !== 'ar' }">
                  <i class="fas fa-dollar-sign text-[#faf6df] text-xl"></i>
                </div>
                <h3 class="text-2xl font-bold text-[#0f4640] mb-4">{{ $t('financial.features.cards.multiCurrency.title') }}</h3>
                <p class="text-gray-600 mb-4">
                  {{ $t('financial.features.cards.multiCurrency.description') }}
                </p>
                <a href="https://admin.salasah.sa/#/login" class="text-[#0f4640] font-semibold hover:text-[#0f4640]/80 no-underline">
                  {{ $t('financial.features.cards.multiCurrency.readMore') }}
                  <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
                </a>
              </div>
            </div>
            <div class="relative h-[300px] overflow-hidden rounded-xl">
              <img 
                src="https://erpnext.com/files/multi-currency-accounting387fe5.png" 
                :alt="$t('financial.features.cards.multiCurrency.title')" 
                class="w-full h-full object-contain bg-gray-50 p-4"
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Customization Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="flex flex-wrap items-center">
          <div class="w-full lg:w-1/2 mb-8 lg:mb-0" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <h2 class="text-3xl font-bold text-[#0f4640] mb-6">{{ $t('financial.customization.title') }}</h2>
            <p class="text-xl text-gray-600 mb-8">
              {{ $t('financial.customization.description') }}
            </p>
            <a href="https://admin.salasah.sa/#/login" class="px-8 py-4 bg-[#0f4640] text-[#faf6df] rounded-xl font-bold hover:bg-opacity-90 transition-all no-underline inline-flex items-center">
              {{ $t('financial.customization.tryNow') }}
              <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
            </a>
          </div>
          <div class="w-full lg:w-1/2 lg:pl-12">
            <img src="https://erpnext.com/files/customize-sales-invoice.png" :alt="$t('financial.customization.title')" 
              class="rounded-2xl shadow-lg w-full hover:scale-105 transition-transform cursor-pointer object-contain bg-white p-4">
          </div>
        </div>
      </div>
    </section>

    <!-- Auto-invoicing Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="flex flex-wrap items-center">
          <div class="w-full lg:w-1/2 mb-8 lg:mb-0" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <h2 class="text-3xl font-bold text-[#0f4640] mb-6">{{ $t('financial.autoInvoicing.title') }}</h2>
            <p class="text-xl text-gray-600 mb-8">
              {{ $t('financial.autoInvoicing.description') }}
            </p>
            <a href="https://admin.salasah.sa/#/login" class="px-8 py-4 bg-[#0f4640] text-[#faf6df] rounded-xl font-bold hover:bg-opacity-90 transition-all no-underline inline-flex items-center">
              {{ $t('financial.autoInvoicing.tryNow') }}
              <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
            </a>
          </div>
          <div class="w-full lg:w-1/2 lg:pl-12">
            <img src="https://erpnext.com/files/subscription-plan.png" :alt="$t('financial.autoInvoicing.title')" 
              class="rounded-2xl shadow-lg w-full hover:scale-105 transition-transform cursor-pointer object-contain bg-white p-4">
          </div>
        </div>
      </div>
    </section>

    <!-- Call to Action Section -->
    <section class="py-16 bg-[#0f4640]">
      <div class="container mx-auto px-4">
        <div class="text-center">
          <h2 class="text-3xl font-bold text-[#faf6df] mb-6">{{ $t('financial.cta.title') }}</h2>
          <p class="text-xl text-white/90 mb-8">{{ $t('financial.cta.subtitle') }}</p>
          <div class="flex gap-4 justify-center">
            <a href="https://admin.salasah.sa/#/login" class="px-8 py-4 border-2 border-[#faf6df] text-[#faf6df] rounded-xl font-bold hover:bg-[#faf6df] hover:text-[#0f4640] transition-all no-underline inline-flex items-center">
              {{ $t('financial.cta.login') }}
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Multilingual Support Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-[#0f4640] mb-6">{{ $t('financial.multilingual.title') }}</h2>
          <p class="text-xl text-gray-600 mb-4">
            {{ $t('financial.multilingual.subtitle') }}
          </p>
          <a href="https://translate.erpnext.com" target="_blank" class="text-[#0f4640] hover:text-[#0f4640]/80">
            translate.erpnext.com
          </a>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div class="bg-white p-6 rounded-xl shadow-lg" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <h3 class="text-xl font-bold mb-2">{{ $t('financial.multilingual.languages.arabic.name') }}</h3>
            <p class="text-gray-600">{{ $t('financial.multilingual.languages.arabic.progress') }}</p>
          </div>
          <div class="bg-white p-6 rounded-xl shadow-lg">
            <h3 class="text-xl font-bold mb-2">{{ $t('financial.multilingual.languages.spanish.name') }}</h3>
            <p class="text-gray-600">{{ $t('financial.multilingual.languages.spanish.progress') }}</p>
          </div>
          <div class="bg-white p-6 rounded-xl shadow-lg">
            <h3 class="text-xl font-bold mb-2">{{ $t('financial.multilingual.languages.german.name') }}</h3>
            <p class="text-gray-600">{{ $t('financial.multilingual.languages.german.progress') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Additional Features Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-[#0f4640] mb-6">{{ $t('financial.additionalFeatures.title') }}</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div class="bg-white p-6 rounded-xl shadow-lg" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <h3 class="text-xl font-bold text-[#0f4640] mb-4">{{ $t('financial.additionalFeatures.cards.accountTree.title') }}</h3>
            <p class="text-gray-600 mb-4">{{ $t('financial.additionalFeatures.cards.accountTree.description') }}</p>
            <a href="https://admin.salasah.sa/#/login" class="text-[#0f4640] hover:text-[#0f4640]/80">
              {{ $t('financial.additionalFeatures.cards.accountTree.readMore') }}
              <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
            </a>
          </div>
          <div class="bg-white p-6 rounded-xl shadow-lg" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <h3 class="text-xl font-bold text-[#0f4640] mb-4">{{ $t('financial.additionalFeatures.cards.journalEntries.title') }}</h3>
            <p class="text-gray-600 mb-4">{{ $t('financial.additionalFeatures.cards.journalEntries.description') }}</p>
            <a href="https://admin.salasah.sa/#/login" class="text-[#0f4640] hover:text-[#0f4640]/80">
              {{ $t('financial.additionalFeatures.cards.journalEntries.readMore') }}
              <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
            </a>
          </div>
          <div class="bg-white p-6 rounded-xl shadow-lg" :class="{ 'text-right': $i18n.locale === 'ar' }">
            <h3 class="text-xl font-bold text-[#0f4640] mb-4">{{ $t('financial.additionalFeatures.cards.bookkeeping.title') }}</h3>
            <p class="text-gray-600 mb-4">{{ $t('financial.additionalFeatures.cards.bookkeeping.description') }}</p>
            <a href="https://admin.salasah.sa/#/login" class="text-[#0f4640] hover:text-[#0f4640]/80">
              {{ $t('financial.additionalFeatures.cards.bookkeeping.readMore') }}
              <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import PageHero from '@/components/PageHero.vue'

export default {
  name: 'FinancialView',
  components: {
    PageHero
  },
  data() {
    return {
      // Component data can go here
    }
  },
  mounted() {
    // Component initialization
    console.log('Financial view loaded')
  }
}
</script>

<style scoped>
.financial {
  overflow-x: hidden;
}

/* Remove underlines from all buttons and links */
button, a {
  text-decoration: none !important;
}

/* Add smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* Add hover effects */
.hover\:shadow-xl:hover {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.hover\:scale-110:hover {
  transform: scale(1.1);
}

/* Image container styles */
.group {
  cursor: pointer;
}

.group:hover .group-hover\:scale-110 {
  transform: scale(1.1);
}

.group:hover .group-hover\:bg-opacity-10 {
  background-color: rgba(0, 0, 0, 0.1);
}

/* Add animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out forwards;
}

/* Responsive image container */
.h-\[400px\] {
  height: 400px;
}

@media (max-width: 768px) {
  .h-\[400px\] {
    height: 300px;
  }
}

/* Image hover overlay */
.group:hover .group-hover\:bg-opacity-10 {
  background-color: rgba(0, 0, 0, 0.1);
}

/* Smooth image zoom */
.transition-transform {
  transition: transform 0.5s ease-out;
}

/* Better shadow effects */
.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.hover\:shadow-xl:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
</style>